import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Link2, RefreshCw, Trash2 } from 'lucide-react';
import { createShareLink, getShareLink, revokeShareLink } from '../../lib/clientsApi';
import { copyText } from '../../lib/clipboard';
import { usePermissions } from '../../hooks/usePermissions';
import { useConfirm } from '../ui/ConfirmProvider';

/**
 * The link (+ PIN) the client opens to see its calendar and approve posts.
 * Both are shown once, right after they are made — only hashes are kept — so
 * "Generează alt link" is also how a lost PIN is replaced.
 */
export function ClientShareLinkCard({ clientId }: { clientId: string }) {
  const { can } = usePermissions();
  const allowed = can('socialPosts', 'canManageShareLinks');
  const qc = useQueryClient();
  const confirm = useConfirm();
  const key = ['clients', clientId, 'share-link'];
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => getShareLink(clientId),
    enabled: allowed,
  });
  const [fresh, setFresh] = useState<{ url: string; pin: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!allowed) return null;

  async function generate() {
    if (
      data?.active &&
      !(await confirm({
        message: 'Linkul de acum nu va mai merge. Generezi unul nou?',
        danger: true,
      }))
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setFresh(await createShareLink(clientId));
      void qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Linkul nu a putut fi generat.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!(await confirm({ message: 'Oprești linkul clientului?', danger: true }))) return;
    setBusy(true);
    try {
      await revokeShareLink(clientId);
      setFresh(null);
      void qc.invalidateQueries({ queryKey: key });
    } finally {
      setBusy(false);
    }
  }

  async function copy(label: string, text: string) {
    if (await copyText(text)) {
      setCopied(label);
      window.setTimeout(() => setCopied(null), 2000);
    }
  }

  const active = data?.active;

  return (
    <section className="rounded-lg border border-gray-200 bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-brand-600/15 p-2 text-brand-600">
            <Link2 size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Linkul clientului</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              {active
                ? `Activ din ${new Date(active.createdAt).toLocaleDateString('ro-RO')}${
                    active.lastUsedAt
                      ? `, deschis ultima dată pe ${new Date(active.lastUsedAt).toLocaleDateString('ro-RO')}`
                      : ', încă nedeschis'
                  }.`
                : 'Clientul își vede calendarul și aprobă postările, fără cont în Firmio.'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void generate()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={14} /> {active ? 'Generează alt link' : 'Generează link'}
          </button>
          {active && (
            <button
              type="button"
              onClick={() => void revoke()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={14} /> Oprește
            </button>
          )}
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {fresh && (
        <div className="mt-3 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="text-amber-800">
            Copiază-le acum: linkul și PIN-ul nu mai pot fi afișate după ce închizi pagina. Trimite
            PIN-ul separat de link (alt mesaj sau telefonic).
          </p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-card px-2 py-1 text-gray-800">
              {fresh.url}
            </code>
            <button
              type="button"
              onClick={() => void copy('url', fresh.url)}
              className="inline-flex items-center gap-1 rounded border border-gray-300 bg-card px-2 py-1 text-gray-700"
            >
              <Copy size={13} /> {copied === 'url' ? 'Copiat' : 'Copiază'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">PIN:</span>
            <code className="rounded bg-card px-2 py-1 text-base font-semibold tracking-widest text-gray-900">
              {fresh.pin}
            </code>
            <button
              type="button"
              onClick={() => void copy('pin', fresh.pin)}
              className="inline-flex items-center gap-1 rounded border border-gray-300 bg-card px-2 py-1 text-gray-700"
            >
              <Copy size={13} /> {copied === 'pin' ? 'Copiat' : 'Copiază'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
